/* -----------------Globals--------------- */
import { useState } from "react";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { FeedbackDialog } from "@features/feedback/components/dialogs/FeedbackDialog";
/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";
/* -----------------UI--------------- */
import Box from "@memorang/ui/src/components/Box";
import Loader from "@memorang/ui/src/components/Loader";
import StudyPackApplet from "@memorang/ui/src/components/StudyPackApplet";
import useBreakpoints from "@memorang/ui/src/useBreakpoints";
import { usePathname, useRouter } from "expo-router";
import { getDashboardData, getStats } from "../helpers/dashboard-data";
import useUserLeaderboard from "../hooks/useUserLeaderboard";

/* -----------------Types--------------- */
import type { MixedItemDist } from "@memorang/types/src/tag";

/* -----------------Child components--------------- */

import { events } from "@constants/tracking";
/* -----------------Analytics--------------- */
import { trackCustomEvent } from "analytics";

import { appBarMeta } from "@constants/appbar";
import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { defaultFeedbackSubtitle } from "@features/feedback/constants";
import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type { QuickActionsChipType } from "@memorang/ui/src/types";
import CustomAppbar from "components/CustomAppbar";
import { useTranslation } from "react-i18next";
import { PurchaseDialog } from "../components/PurchaseDialog";
import { getQuickActionsChips, getWeakTagsIds } from "../helpers/common";

const StudyPackContainer = ({
	showBackButton = true,
}: {
	showBackButton?: boolean;
}) => {
	const { isMobile } = useDevice();
	const showStudyPackAsHome = useAppStore((store) => store.showStudyPackAsHome);

	const { data: mixedItemDistResponse } = useMixedItemDist(
		true,
		"GET_STUDYPACK_DISTRIBUTION",
	);

	const {
		currentExamContext: { examName },
	} = useExamContext();

	const { smDown } = useBreakpoints();
	const finalHeaderTitle = smDown ? examName : appBarMeta["product-home"].title;

	const finalSubtitle = smDown ? "" : appBarMeta["product-home"].description;

	const customProps = showStudyPackAsHome
		? {
				mode: isWeb ? ("medium" as const) : ("small" as const),
				align: "flex-start" as const,
			}
		: {};

	const CustomLoader = () => {
		return (
			<Box flex={1}>
				<CustomAppbar
					back={showBackButton}
					{...customProps}
					options={{
						headerTitle: finalHeaderTitle,
						subtitle: finalSubtitle,
						...(isMobile && { headerRight: () => null }),
					}}
					role="banner"
				/>
				<Loader />
			</Box>
		);
	};

	if (!mixedItemDistResponse) {
		return <CustomLoader />;
	}

	return (
		<>
			{mixedItemDistResponse && (
				<StudyPackRenderer
					mixedItemDistResponse={mixedItemDistResponse}
					headerTitle={finalHeaderTitle}
					headerSubtitle={finalSubtitle}
					showBackButton={showBackButton}
				/>
			)}
		</>
	);
};

export default StudyPackContainer;

const StudyPackRenderer = ({
	mixedItemDistResponse,
	headerTitle,
	headerSubtitle,
	showBackButton,
}: {
	mixedItemDistResponse: MixedItemDist;
	headerTitle: string;
	headerSubtitle: string | React.ReactNode;
	showBackButton?: boolean;
}) => {
	const { newUserInfo: userInfo } = useAuthContext();

	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);
	const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const { handleFeedbackNavigation } = useCustomNavigation();
	const { t } = useTranslation();
	const pathname = usePathname();
	const toggleShowFeedbackDialog = () => {
		setShowFeedbackDialog((prev) => !prev);
	};
	const toggleShowSessionConfigDialog = () => {
		setShowSessionConfigDialog((prev) => !prev);
	};
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();
	const { isMobile, isMediumWindowSize } = useDevice();
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

	const theme = useAppTheme();
	const toggleShowPurchaseDialog = () => {
		setShowPurchaseDialog((prev) => !prev);
	};

	const showStudyPackAsHome = useAppStore((store) => store.showStudyPackAsHome);
	const groupByCategory = useAppStore((store) => store.groupByCategory);

	const router = useRouter();
	const data = mixedItemDistResponse;

	const handleNavigateToIAP = () => {
		trackCustomEvent({
			eventName: events.upgradeChipClicked,
		});
		if (isMobile || isMediumWindowSize) {
			router.push({
				pathname: "/purchase",
				params: {
					numItems,
					productType: "STUDY_PACK",
				},
			});
		} else {
			toggleShowPurchaseDialog();
		}
	};
	const {
		distribution,
		productImage,
		productTitle,
		sectionTags,
		numItems,
		masteryPercentage,
	} = getDashboardData(data, handleNavigateToIAP, groupByCategory);

	const weakTagsIds = getWeakTagsIds(data);

	const handleQuickActionPress = (action: QuickActionsChipType["type"]) => {
		trackCustomEvent({
			eventName: events.quickActionClicked,
			action,
		});
		switch (action) {
			case "weak":
				router.push({
					pathname: "/(protected)/session/customize",
					params: {
						selectedTagIds: weakTagsIds,
					},
				});
				break;
			case "leaderboard":
				router.push("/(protected)/(tabs)/leaderboard");
				break;
			case "progress":
				router.push("/(protected)/(tabs)/progress");
				break;
			default:
				break;
		}
	};

	//TODO:Below hooks look ugly to get just the leaderboard work, instead the api should return the stats

	const { userLeaderboardResponse } = useUserLeaderboard("all_time");

	const numAnswered = Object.entries(distribution).reduce(
		(acc, [key, value]) => (key !== "none" ? acc + value : acc),
		0,
	);
	const handleClickListItem = (selectedTagId: number, locked?: boolean) => {
		if (locked) {
			handleNavigateToIAP();
		} else if (isWeb) {
			if (isMobile) {
				router.push({
					pathname: "/session/customize",
					params: {
						selectedTagIds: [selectedTagId],
					},
				});
			} else {
				setSelectedTagId(selectedTagId);
				toggleShowSessionConfigDialog();
			}
		} else {
			router.push({
				pathname: "/session/customize",
				params: {
					selectedTagIds: [selectedTagId],
				},
			});
		}
	};
	const stats = getStats({
		masteryPercentage,
		numAnswered,
		xp: userLeaderboardResponse?.xp,
		isDarkMode: theme.dark,
	});
	const actionChips = getQuickActionsChips(studyPackCollectionId);

	const customProps = showStudyPackAsHome
		? {
				align: "flex-start" as const,
			}
		: {};
	return (
		<>
			<StudyPackApplet
				stats={stats}
				distribution={distribution}
				productTitle={productTitle}
				productImage={productImage}
				sectionTags={sectionTags}
				handleNavigateToIAP={handleNavigateToIAP}
				handleQuickActionPress={handleQuickActionPress}
				bottomFab={{
					icon: "play-outline",
					label: "Quick Study",
					onPress: () => router.push("/(protected)/session/customize"),
				}}
				actionChips={actionChips}
				handleClickListItem={handleClickListItem}
				handleFeedbackNavigation={() =>
					handleFeedbackNavigation(toggleShowFeedbackDialog)
				}
				customNavigationBarProps={{
					back: showBackButton,
					onBackPress: () => {
						if (router.canGoBack()) {
							router.back();
						} else {
							router.push("/(protected)/(tabs)/home");
						}
					},
					align: customProps.align,
					onAvatarPress: () => router.push("/(protected)/account/settings"),
					userInfo: {
						firstName: userInfo?.firstName || userInfo?.name,
						lastName: userInfo?.lastName,
					},
					stats: stats,
					handleFeedbackNavigation,
					toggleShowFeedbackDialog,
					options: {
						headerTitle,
						subtitle: headerSubtitle,
						...(isMobile && { headerRight: () => null }),
					},
				}}
			/>

			{showPurchaseDialog ? (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog}
					productType={"STUDY_PACK"}
				/>
			) : null}
			{isWeb && showFeedbackDialog && (
				<FeedbackDialog
					visible={showFeedbackDialog}
					view={pathname}
					feedbackSubtitle={t(defaultFeedbackSubtitle)}
					handleDismiss={toggleShowFeedbackDialog}
				/>
			)}
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleShowSessionConfigDialog}
					selectedTagIds={[selectedTagId!]}
				/>
			) : null}
		</>
	);
};
